import React from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis,
  Tooltip, Cell, BarChart,
} from "recharts";
import { getZoneMarketContext } from "@/lib/vg2Valuta";
import { fmtEur } from "@/lib/vg2Format";

// §M8.4 · nessun gergo interno (codici zona, semestri tecnici, linkzona):
// il backend restituisce solo etichette pulite. Qui non si inventa nulla.
// VISIBILITÀ: le soglie stanno nel backend (status "trend"/"available"
// vs "hidden"); qui le voci sotto soglia NON vengono proprio renderizzate
// (niente grafico mono-semestre, niente messaggi "non disponibile").

class _Boundary extends React.Component {
  constructor(p) { super(p); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch() { /* isolata: non deve rompere il risultato */ }
  render() {
    if (this.state.crashed) return null; // fallimento → sezione assente
    return this.props.children;
  }
}

const SectionTitle = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
    {children}
  </p>
);

function PositioningBand({ ctx }) {
  const band = ctx.current_band;
  const est = ctx.estimate || {};
  const isRent = (ctx.unit || "").includes("month");
  const u = isRent ? "€/m²/mese" : "€/m²";
  const central = est.central;
  const span = Math.max(band.max - band.min, 1);
  const pos = central != null
    ? Math.max(0, Math.min(100, ((central - band.min) / span) * 100))
    : null;
  return (
    <div data-testid="mercato-positioning">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm text-vg2-ink">La tua stima</span>
        <span className="text-sm font-semibold text-vg2-red"
              data-testid="mercato-positioning-estimate">
          {central != null ? fmtEur(central) : "—"} <span className="text-vg2-muted font-normal">{u}</span>
        </span>
      </div>
      <div className="relative h-3 rounded-full bg-vg2-subtle"
           data-testid="mercato-positioning-bar">
        <div className="absolute inset-y-0 left-0 right-0 rounded-full bg-vg2-line/70" />
        {pos != null && (
          <div className="absolute -top-1 h-5 w-[3px] bg-vg2-red rounded"
               style={{ left: `calc(${pos}% - 1px)` }}
               data-testid="mercato-positioning-marker" />
        )}
      </div>
      <div className="flex justify-between mt-2 text-xs text-vg2-muted">
        <span data-testid="mercato-band-min">{fmtEur(band.min)} {u}</span>
        <span>Fascia di zona · {band.period_label}</span>
        <span data-testid="mercato-band-max">{fmtEur(band.max)} {u}</span>
      </div>
      <p className="mt-3 text-sm text-vg2-ink" data-testid="mercato-positioning-note">
        La stima del tuo immobile tiene già conto di questa zona: il confronto
        qui sopra è un inquadramento rispetto alla fascia di mercato locale,
        non una verifica indipendente.
      </p>
    </div>
  );
}

function HistoryChart({ ctx }) {
  const h = ctx.history || {};
  const isRent = (ctx.unit || "").includes("month");
  const u = isRent ? "€/m²/mese" : "€/m²";
  const rows = (h.series || []).map((s) => ({
    label: s.period_label,
    base: s.min,
    span: Math.max((s.max || 0) - (s.min || 0), 0),
    min: s.min, max: s.max,
  }));
  return (
    <div data-testid="mercato-history-chart">
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0}
                 angle={-15} textAnchor="end" height={48} />
          <YAxis tick={{ fontSize: 11 }} width={52}
                 tickFormatter={(v) => fmtEur(v)} />
          <Tooltip
            formatter={(val, name, p) => {
              const r = p && p.payload;
              return [`${fmtEur(r.min)} – ${fmtEur(r.max)} ${u}`, "Fascia di zona"];
            }}
            labelFormatter={(l) => l} />
          <Bar dataKey="base" stackId="band" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="span" stackId="band" fill="#b91c1c" radius={[3, 3, 0, 0]}
               isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
      {h.discontinuity && (
        <p className="mt-2 text-xs text-vg2-muted" data-testid="mercato-history-discontinuity">
          Alcuni semestri precedenti non sono confrontabili o mancano: mostriamo
          solo il tratto continuo più recente, senza unire serie diverse.
        </p>
      )}
      <p className="mt-2 text-xs text-vg2-muted">
        Le quotazioni dei diversi semestri possono non essere sempre
        confrontabili fra loro per cambi metodologici o di perimetro delle zone.
      </p>
    </div>
  );
}

function VolumesChart({ ctx }) {
  const v = ctx.municipality_volumes || {};
  const rows = (v.series || []).map((s) => ({ label: s.period_label, ntn: s.ntn }));
  return (
    <div data-testid="mercato-volumes-chart">
      <p className="text-xs text-vg2-muted mb-2">
        Dato comunale{ctx.municipality_label ? ` (${ctx.municipality_label})` : ""},
        non di zona. Conteggio delle compravendite (NTN), non prezzi.
      </p>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={44} />
          <Tooltip formatter={(val) => [val, "Compravendite (NTN)"]} />
          <Bar dataKey="ntn" fill="#1f2937" radius={[3, 3, 0, 0]}
               isAnimationActive={false}>
            {rows.map((_, i) => <Cell key={i} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function MercatoInner({ caseId, analysisId }) {
  const [ctx, setCtx] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getZoneMarketContext(caseId, analysisId);
        if (alive) setCtx(data);
      } catch {
        if (alive) setFailed(true); // isolata: non blocca il resto
      }
    })();
    return () => { alive = false; };
  }, [caseId, analysisId]);

  if (failed || !ctx) return null;
  // sezione nascosta del tutto se manca il posizionamento (nessun titolo orfano,
  // nessun "non disponibile"): compare solo quando c'è una banda OMI reale.
  const band = ctx.available && ctx.current_band
    && ctx.current_band.min != null && ctx.current_band.max != null;
  if (!band) return null;

  const showHistory = (ctx.history || {}).status === "trend"
    && (ctx.history.series || []).length > 0;
  const showVolumes = (ctx.municipality_volumes || {}).status === "available"
    && (ctx.municipality_volumes.series || []).length > 0;

  return (
    <section className="mb-12" data-testid="mercato-intorno">
      <SectionTitle>Il mercato intorno al tuo immobile</SectionTitle>

      {/* Posizionamento della stima rispetto al mercato di zona (sempre) */}
      <div className={showHistory || showVolumes ? "mb-8" : ""}>
        <p className="text-sm font-medium text-vg2-ink mb-3"
           data-testid="mercato-positioning-heading">
          Dove si colloca la tua stima
          {ctx.zone_label ? ` · ${ctx.zone_label}` : ""}
          {ctx.property_type_label ? ` · ${ctx.property_type_label}` : ""}
          {ctx.condition_label ? ` · stato ${ctx.condition_label.toLowerCase()}` : ""}
        </p>
        <PositioningBand ctx={ctx} />
      </div>

      {/* Storico OMI di zona — solo se serie sufficiente e continua */}
      {showHistory && (
        <div className="mb-8">
          <p className="text-sm font-medium text-vg2-ink mb-3"
             data-testid="mercato-history-heading">
            Andamento storico delle quotazioni di zona (€/m²)
          </p>
          <HistoryChart ctx={ctx} />
        </div>
      )}

      {/* Compravendite comunali (NTN) — solo se periodi sufficienti */}
      {showVolumes && (
        <div className="mb-6">
          <p className="text-sm font-medium text-vg2-ink mb-3"
             data-testid="mercato-volumes-heading">
            Attività delle compravendite nel Comune
          </p>
          <VolumesChart ctx={ctx} />
        </div>
      )}

      {ctx.attribution && (
        <p className="text-xs text-vg2-muted" data-testid="mercato-attribution">
          {ctx.attribution}
        </p>
      )}
      <ul className="mt-2 space-y-1" data-testid="mercato-disclaimers">
        {(ctx.disclaimers || []).map((d, i) => (
          <li key={i} className="text-xs text-vg2-muted">• {d}</li>
        ))}
      </ul>
    </section>
  );
}

export function MercatoIntornoBlock({ caseId, analysisId }) {
  if (!caseId || !analysisId) return null;
  return (
    <_Boundary>
      <MercatoInner caseId={caseId} analysisId={analysisId} />
    </_Boundary>
  );
}

export default MercatoIntornoBlock;
